import { SyntheticEvent, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Skeleton,
  lighten,
  type Theme,
  type SxProps,
} from '@mui/material';

import { DriveIcon, Typography, Button } from '@occc/ui-design-system';

import { BetaBadge } from './beta-badge.component';
import { useRecentConversations } from './hooks/use-recent-conversations.hook';

import { routeCreator } from '../../drive-insights.router';

type SidebarButtonStyleOptions = {
  isActive: boolean;
  minHeightMultiplier?: number;
  gap?: number;
  overflow?: string;
  alwaysHighlighted?: boolean;
};

const getSidebarButtonStyles =
  ({
    isActive,
    minHeightMultiplier = 5,
    gap,
    overflow,
    alwaysHighlighted,
  }: SidebarButtonStyleOptions): SxProps<Theme> =>
  (theme) => ({
    display: 'flex',
    minHeight: theme.spacing(minHeightMultiplier),
    pl: 2,
    gap,
    textTransform: 'none',
    letterSpacing: 0,
    justifyContent: 'start',
    borderRadius: 2,
    fontSize: theme.typography.body2.fontSize,
    fontWeight: isActive
      ? theme.typography.fontWeightBold
      : alwaysHighlighted
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
    color:
      isActive || alwaysHighlighted ? theme.palette.primary.main : theme.palette.text.secondary,
    background: isActive ? lighten(theme.palette.primary.main, 0.9) : null,
    lineHeight: theme.typography.body2.lineHeight,
    alignItems: 'center',
    overflow,
    '&:hover': {
      background: isActive
        ? lighten(theme.palette.primary.main, 0.9)
        : lighten(theme.palette.background.default, 0.5),
    },
  });

type SidebarButtonProps = {
  linkTo: string;
  isActive: boolean;
  styleOptions?: Omit<SidebarButtonStyleOptions, 'isActive'>;
  children: ReactNode;
};

const SidebarButton = ({ linkTo, isActive, styleOptions = {}, children }: SidebarButtonProps) => {
  return (
    <Button
      component={RouterLink}
      to={linkTo}
      sx={getSidebarButtonStyles({ isActive, ...styleOptions })}
      fullWidth
    >
      {children}
    </Button>
  );
};

const SidebarLinkItem = ({
  title,
  linkTo,
  isActive,
  alwaysHighlighted,
}: {
  title: string;
  linkTo: string;
  isActive: boolean;
  alwaysHighlighted?: boolean;
}) => {
  return (
    <SidebarButton linkTo={linkTo} isActive={isActive} styleOptions={{ alwaysHighlighted }}>
      {title}
    </SidebarButton>
  );
};

const SidebarConversationItem = ({
  title,
  conversationId,
  isActive,
}: {
  title: string;
  conversationId: string;
  isActive: boolean;
}) => {
  const linkTo = `${routeCreator.base()}?conversationId=${conversationId}`;

  return (
    <SidebarButton
      linkTo={linkTo}
      isActive={isActive}
      styleOptions={{ minHeightMultiplier: 4.5, overflow: 'hidden' }}
    >
      <Typography noWrap component='span' sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
        {title}
      </Typography>
    </SidebarButton>
  );
};

export const DriveInsightsSidebar = () => {
  const { pathname, search } = useLocation();
  const { conversations, loading } = useRecentConversations();

  const searchParams = new URLSearchParams(search);
  const activeConversationId = searchParams.get('conversationId');

  const isOnDriveInsights = pathname.startsWith(routeCreator.base());
  const isOnHistoryPage = pathname === routeCreator.history();
  const isOnInsightsPage = pathname === routeCreator.base();

  const [isExpanded, setExpanded] = useState(isOnDriveInsights);
  const prevIsOnDriveInsights = useRef(isOnDriveInsights);

  const handleChange = (_: SyntheticEvent, value: boolean) => {
    setExpanded(value);
  };

  useEffect(() => {
    if (isOnDriveInsights && !prevIsOnDriveInsights.current) {
      setExpanded(true);
    }
    prevIsOnDriveInsights.current = isOnDriveInsights;
  }, [isOnDriveInsights]);

  const hasConversations = conversations.length > 0;
  const shouldShowEmptyState =
    !loading && !hasConversations && isOnInsightsPage && !activeConversationId;

  return (
    <Box>
      <Accordion onChange={handleChange} expanded={isExpanded} square disableGutters>
        <AccordionSummary sx={{ pl: 1, pr: 0 }}>
          <Stack
            onClick={(event) => {
              event.stopPropagation();
            }}
            component={RouterLink}
            to={routeCreator.base()}
            direction='row'
            alignItems='center'
            spacing={2}
            sx={{
              textDecoration: 'none',
              width: '100%',
              color: isOnDriveInsights ? 'primary.main' : 'text.disabled',
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            <DriveIcon />
            <Typography
              sx={(theme) => ({
                color: isOnDriveInsights ? 'primary.main' : 'text.secondary',
                fontWeight: isOnDriveInsights
                  ? theme.typography.fontWeightBold
                  : theme.typography.fontWeightMedium,
                '&:hover': {
                  color: 'primary.main',
                },
              })}
            >
              Insights
            </Typography>
          </Stack>
          <BetaBadge />
        </AccordionSummary>

        <AccordionDetails sx={{ pl: 4, pr: 0, display: 'block' }}>
          {loading && !hasConversations && (
            <Stack spacing={1} py={1}>
              <Skeleton variant='text' width='80%' sx={{ height: (theme) => theme.spacing(3) }} />
              <Skeleton variant='text' width='70%' sx={{ height: (theme) => theme.spacing(3) }} />
              <Skeleton variant='text' width='85%' sx={{ height: (theme) => theme.spacing(3) }} />
            </Stack>
          )}

          {hasConversations && (
            <>
              {conversations.map((conversation) => (
                <SidebarConversationItem
                  key={conversation.id}
                  title={conversation.name}
                  conversationId={conversation.id}
                  isActive={activeConversationId === conversation.id}
                />
              ))}
            </>
          )}

          <SidebarLinkItem
            title='View full history...'
            linkTo={routeCreator.history()}
            isActive={isOnHistoryPage}
            alwaysHighlighted
          />

          {shouldShowEmptyState && (
            <Typography variant='body2' sx={{ pl: 2, py: 1, color: 'text.disabled' }}>
              Start a conversation to see it here
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
